// =============================================================================
// BP-05-03: Calendar Frontend - Zustand Store
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  CalendarView,
  AppointmentType,
  AppointmentStatus,
  type AppointmentSummary,
  type CalendarDateRange,
} from '../types';

// -----------------------------------------------------------------------------
// State Types
// -----------------------------------------------------------------------------

interface CalendarState {
  // View state
  currentView: CalendarView;
  currentDate: Date;
  selectedDate: Date | null;

  // Filter state
  selectedChildId: string | null;
  selectedAppointmentTypes: AppointmentType[];
  selectedStatuses: AppointmentStatus[];

  // UI state
  isSidebarOpen: boolean;
  isFormOpen: boolean;
  isDetailOpen: boolean;
  selectedAppointmentId: string | null;
  editingAppointmentId: string | null;

  // Drag and drop state
  isDragging: boolean;
  draggedAppointment: AppointmentSummary | null;

  // Quick add state
  quickAddSlot: { start: Date; end: Date } | null;

  // Settings
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Sunday, 1 = Monday
  timeFormat: '12h' | '24h';
  defaultDuration: number; // minutes
  showWeekends: boolean;
  showDeclinedEvents: boolean;
}

interface CalendarActions {
  // View actions
  setView: (view: CalendarView) => void;
  setCurrentDate: (date: Date) => void;
  goToToday: () => void;
  goToNext: () => void;
  goToPrevious: () => void;
  selectDate: (date: Date | null) => void;

  // Filter actions
  setSelectedChildId: (childId: string | null) => void;
  toggleAppointmentType: (type: AppointmentType) => void;
  toggleStatus: (status: AppointmentStatus) => void;
  clearFilters: () => void;

  // UI actions
  toggleSidebar: () => void;
  openForm: (appointmentId?: string) => void;
  closeForm: () => void;
  openDetail: (appointmentId: string) => void;
  closeDetail: () => void;

  // Drag and drop actions
  startDrag: (appointment: AppointmentSummary) => void;
  endDrag: () => void;

  // Quick add actions
  setQuickAddSlot: (slot: { start: Date; end: Date } | null) => void;

  // Settings actions
  setWeekStartsOn: (day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => void;
  setTimeFormat: (format: '12h' | '24h') => void;
  setDefaultDuration: (minutes: number) => void;
  toggleWeekends: () => void;
  toggleDeclinedEvents: () => void;

  // Utility
  getDateRange: () => CalendarDateRange;
  reset: () => void;
}

type CalendarStore = CalendarState & CalendarActions;

// -----------------------------------------------------------------------------
// Initial State
// -----------------------------------------------------------------------------

const initialState: CalendarState = {
  currentView: CalendarView.MONTH,
  currentDate: new Date(),
  selectedDate: null,
  selectedChildId: null,
  selectedAppointmentTypes: [],
  selectedStatuses: [],
  isSidebarOpen: true,
  isFormOpen: false,
  isDetailOpen: false,
  selectedAppointmentId: null,
  editingAppointmentId: null,
  isDragging: false,
  draggedAppointment: null,
  quickAddSlot: null,
  weekStartsOn: 0,
  timeFormat: '12h',
  defaultDuration: 60,
  showWeekends: true,
  showDeclinedEvents: false,
};

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function startOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

function startOfWeek(date: Date, weekStartsOn: number): Date {
  const result = new Date(date);
  const day = result.getDay();
  const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  result.setDate(result.getDate() - diff);
  return startOfDay(result);
}

function endOfWeek(date: Date, weekStartsOn: number): Date {
  const result = startOfWeek(date, weekStartsOn);
  result.setDate(result.getDate() + 6);
  result.setHours(23, 59, 59, 999);
  return result;
}

function startOfMonth(date: Date): Date {
  const result = new Date(date);
  result.setDate(1);
  return startOfDay(result);
}

function endOfMonth(date: Date): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + 1, 0);
  result.setHours(23, 59, 59, 999);
  return result;
}

// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------

export const useCalendarStore = create<CalendarStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // View actions
      setView: (view) => set({ currentView: view }),

      setCurrentDate: (date) => set({ currentDate: date }),

      goToToday: () => set({ currentDate: new Date() }),

      goToNext: () => {
        const { currentView, currentDate } = get();
        let newDate: Date;

        switch (currentView) {
          case CalendarView.DAY:
            newDate = addDays(currentDate, 1);
            break;
          case CalendarView.WEEK:
            newDate = addWeeks(currentDate, 1);
            break;
          case CalendarView.MONTH:
          case CalendarView.AGENDA:
            newDate = addMonths(currentDate, 1);
            break;
          default:
            newDate = currentDate;
        }

        set({ currentDate: newDate });
      },

      goToPrevious: () => {
        const { currentView, currentDate } = get();
        let newDate: Date;

        switch (currentView) {
          case CalendarView.DAY:
            newDate = addDays(currentDate, -1);
            break;
          case CalendarView.WEEK:
            newDate = addWeeks(currentDate, -1);
            break;
          case CalendarView.MONTH:
          case CalendarView.AGENDA:
            newDate = addMonths(currentDate, -1);
            break;
          default:
            newDate = currentDate;
        }

        set({ currentDate: newDate });
      },

      selectDate: (date) => set({ selectedDate: date }),

      // Filter actions
      setSelectedChildId: (childId) => set({ selectedChildId: childId }),

      toggleAppointmentType: (type) => {
        const { selectedAppointmentTypes } = get();
        const newTypes = selectedAppointmentTypes.includes(type)
          ? selectedAppointmentTypes.filter((t) => t !== type)
          : [...selectedAppointmentTypes, type];
        set({ selectedAppointmentTypes: newTypes });
      },

      toggleStatus: (status) => {
        const { selectedStatuses } = get();
        const newStatuses = selectedStatuses.includes(status)
          ? selectedStatuses.filter((s) => s !== status)
          : [...selectedStatuses, status];
        set({ selectedStatuses: newStatuses });
      },

      clearFilters: () =>
        set({
          selectedChildId: null,
          selectedAppointmentTypes: [],
          selectedStatuses: [],
        }),

      // UI actions
      toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

      openForm: (appointmentId) =>
        set({
          isFormOpen: true,
          editingAppointmentId: appointmentId || null,
          isDetailOpen: false,
        }),

      closeForm: () =>
        set({
          isFormOpen: false,
          editingAppointmentId: null,
          quickAddSlot: null,
        }),

      openDetail: (appointmentId) =>
        set({
          isDetailOpen: true,
          selectedAppointmentId: appointmentId,
          isFormOpen: false,
        }),

      closeDetail: () =>
        set({
          isDetailOpen: false,
          selectedAppointmentId: null,
        }),

      // Drag and drop actions
      startDrag: (appointment) =>
        set({
          isDragging: true,
          draggedAppointment: appointment,
        }),

      endDrag: () =>
        set({
          isDragging: false,
          draggedAppointment: null,
        }),

      // Quick add actions
      setQuickAddSlot: (slot) =>
        set({
          quickAddSlot: slot,
          isFormOpen: slot !== null,
        }),

      // Settings actions
      setWeekStartsOn: (day) => set({ weekStartsOn: day }),
      setTimeFormat: (format) => set({ timeFormat: format }),
      setDefaultDuration: (minutes) => set({ defaultDuration: minutes }),
      toggleWeekends: () => set((state) => ({ showWeekends: !state.showWeekends })),
      toggleDeclinedEvents: () =>
        set((state) => ({ showDeclinedEvents: !state.showDeclinedEvents })),

      // Utility
      getDateRange: () => {
        const { currentView, currentDate, weekStartsOn } = get();
        let start: Date;
        let end: Date;

        switch (currentView) {
          case CalendarView.DAY:
            start = startOfDay(currentDate);
            end = new Date(start);
            end.setHours(23, 59, 59, 999);
            break;
          case CalendarView.WEEK:
            start = startOfWeek(currentDate, weekStartsOn);
            end = endOfWeek(currentDate, weekStartsOn);
            break;
          case CalendarView.MONTH:
          case CalendarView.AGENDA:
            // Include days from previous/next month that are visible
            start = startOfWeek(startOfMonth(currentDate), weekStartsOn);
            end = endOfWeek(endOfMonth(currentDate), weekStartsOn);
            break;
          default:
            start = startOfMonth(currentDate);
            end = endOfMonth(currentDate);
        }

        return {
          start: start.toISOString(),
          end: end.toISOString(),
        };
      },

      reset: () => set(initialState),
    }),
    {
      name: 'brightpath-calendar-store',
      partialize: (state) => ({
        currentView: state.currentView,
        weekStartsOn: state.weekStartsOn,
        timeFormat: state.timeFormat,
        defaultDuration: state.defaultDuration,
        showWeekends: state.showWeekends,
        showDeclinedEvents: state.showDeclinedEvents,
        isSidebarOpen: state.isSidebarOpen,
      }),
    }
  )
);

// -----------------------------------------------------------------------------
// Selectors
// -----------------------------------------------------------------------------

export const selectCurrentView = (state: CalendarStore) => state.currentView;
export const selectCurrentDate = (state: CalendarStore) => state.currentDate;
export const selectFilters = (state: CalendarStore) => ({
  childId: state.selectedChildId,
  appointmentTypes: state.selectedAppointmentTypes,
  statuses: state.selectedStatuses,
});
export const selectIsFiltered = (state: CalendarStore) =>
  state.selectedChildId !== null ||
  state.selectedAppointmentTypes.length > 0 ||
  state.selectedStatuses.length > 0;
export const selectSettings = (state: CalendarStore) => ({
  weekStartsOn: state.weekStartsOn,
  timeFormat: state.timeFormat,
  defaultDuration: state.defaultDuration,
  showWeekends: state.showWeekends,
  showDeclinedEvents: state.showDeclinedEvents,
});
